import {
  PUBLIC_TIER_LABELS,
  PUBLIC_TIER_MONTHLY_PRICES_AUD,
  PUBLIC_TIER_PRICE_IDS,
  isPublicTier,
} from "../../../shared/subscriptionPlans";
import {
  getCombinedMonthlyPriceAud,
  getCombinedPlanLabel,
  type DomainPlanShape,
} from "../../../shared/domainPlans";

export const PRICE_BY_ID: Record<string, number> = {
  // Basic Hosting
  [PUBLIC_TIER_PRICE_IDS.basic]: PUBLIC_TIER_MONTHLY_PRICES_AUD.basic,
  // Essential
  [PUBLIC_TIER_PRICE_IDS.essential]: PUBLIC_TIER_MONTHLY_PRICES_AUD.essential,
  price_1Shmkw53YIqi52EpPzMyMKmn: 50,
  price_1Shmnc53YIqi52EpP2ZiH5fs: 40,
  price_1Shmo153YIqi52EpN0q0ZYIM: 30,
  // Premium
  [PUBLIC_TIER_PRICE_IDS.premium]: PUBLIC_TIER_MONTHLY_PRICES_AUD.premium,
  price_1ShmpV53YIqi52EpkBu3vC36: 90,
  price_1Shmqc53YIqi52EpuIfREZGs: 85,
  price_1Shmpn53YIqi52EpN1nyoIab: 80,
  // Dev catalog
  price_1TEQcM53YIqi52EprG0AsuhI: 5,
  price_1TEQcM53YIqi52EpeDlWZXJX: 10,
  price_1TEQcM53YIqi52Epx04RTQTK: 15,
  price_1TEQcM53YIqi52EpQCYFeWBX: 20,
  price_1TEQcM53YIqi52EpgXhq7Ue7: 25,
};

export const TIER_LABEL_BY_PRICE_ID: Record<string, string> = {
  [PUBLIC_TIER_PRICE_IDS.basic]: PUBLIC_TIER_LABELS.basic,
  [PUBLIC_TIER_PRICE_IDS.essential]: PUBLIC_TIER_LABELS.essential,
  price_1Shmkw53YIqi52EpPzMyMKmn: "Essential",
  price_1Shmnc53YIqi52EpP2ZiH5fs: "Essential",
  price_1Shmo153YIqi52EpN0q0ZYIM: "Essential",
  [PUBLIC_TIER_PRICE_IDS.premium]: PUBLIC_TIER_LABELS.premium,
  price_1ShmpV53YIqi52EpkBu3vC36: "Premium",
  price_1Shmqc53YIqi52EpuIfREZGs: "Premium",
  price_1Shmpn53YIqi52EpN1nyoIab: "Premium",
  price_1TEQcM53YIqi52EprG0AsuhI: "Dev",
  price_1TEQcM53YIqi52EpeDlWZXJX: "Dev",
  price_1TEQcM53YIqi52Epx04RTQTK: "Dev",
  price_1TEQcM53YIqi52EpQCYFeWBX: "Dev",
  price_1TEQcM53YIqi52EpgXhq7Ue7: "Dev",
};

export const TIER_VARIANT_BY_PRICE_ID: Record<string, "default" | "secondary"> = {
  [PUBLIC_TIER_PRICE_IDS.basic]: "secondary",
  [PUBLIC_TIER_PRICE_IDS.essential]: "secondary",
  price_1Shmkw53YIqi52EpPzMyMKmn: "secondary",
  price_1Shmnc53YIqi52EpP2ZiH5fs: "secondary",
  price_1Shmo153YIqi52EpN0q0ZYIM: "secondary",
  [PUBLIC_TIER_PRICE_IDS.premium]: "default",
  price_1ShmpV53YIqi52EpkBu3vC36: "default",
  price_1Shmqc53YIqi52EpuIfREZGs: "default",
  price_1Shmpn53YIqi52EpN1nyoIab: "default",
  price_1TEQcM53YIqi52EprG0AsuhI: "secondary",
  price_1TEQcM53YIqi52EpeDlWZXJX: "secondary",
  price_1TEQcM53YIqi52Epx04RTQTK: "secondary",
  price_1TEQcM53YIqi52EpQCYFeWBX: "secondary",
  price_1TEQcM53YIqi52EpgXhq7Ue7: "secondary",
};

export const DEFAULT_PRICE_PER_DOMAIN = 185;
export const DEFAULT_TIER_LABEL = "Premium";

type DomainPricingShape = DomainPlanShape & {
  stripePriceId?: string | null;
  promo_price?: number | null;
  promoPrice?: number | null;
};

const toPlanShape = (domain: DomainPricingShape): DomainPlanShape => ({
  tier: domain.tier,
  stripe_price_id: domain.stripe_price_id || domain.stripePriceId || null,
  security_addon_tier: domain.security_addon_tier,
  security_addon_price_id: domain.security_addon_price_id,
  stripe_subscription_id: domain.stripe_subscription_id,
  security_addon_subscription_id: domain.security_addon_subscription_id,
});

export const getDomainPrice = (domain: DomainPricingShape) => {
  const combinedPrice = getCombinedMonthlyPriceAud(toPlanShape(domain));
  if (typeof combinedPrice === "number") {
    return combinedPrice;
  }
  if (isPublicTier(domain.tier)) {
    return PUBLIC_TIER_MONTHLY_PRICES_AUD[domain.tier];
  }
  const promoValue =
    typeof domain.promo_price === "number"
      ? domain.promo_price
      : typeof domain.promoPrice === "number"
      ? domain.promoPrice
      : null;
  if (typeof promoValue === "number" && promoValue > 0) {
    return promoValue;
  }
  const priceId = domain.stripe_price_id || domain.stripePriceId || "";
  if (PRICE_BY_ID[priceId] !== undefined) return PRICE_BY_ID[priceId];
  if (domain.tier === "dev") return 5;
  return DEFAULT_PRICE_PER_DOMAIN;
};

export const getDomainLabel = (domain: DomainPricingShape) => {
  const combinedLabel = getCombinedPlanLabel(toPlanShape(domain));
  if (combinedLabel && (domain.tier === "basic" || domain.security_addon_tier)) {
    const promoValue =
      typeof domain.promo_price === "number"
        ? domain.promo_price
        : typeof domain.promoPrice === "number"
        ? domain.promoPrice
        : null;
    if (typeof promoValue === "number" && promoValue > 0) {
      return `${combinedLabel} (Promo)`;
    }
    return combinedLabel;
  }
  if (isPublicTier(domain.tier)) {
    const promoValue =
      typeof domain.promo_price === "number"
        ? domain.promo_price
        : typeof domain.promoPrice === "number"
        ? domain.promoPrice
        : null;
    const base = PUBLIC_TIER_LABELS[domain.tier];
    if (typeof promoValue === "number" && promoValue > 0) {
      return `${base} (Promo)`;
    }
    return base;
  }
  const priceId = domain.stripe_price_id || domain.stripePriceId || "";
  const base = TIER_LABEL_BY_PRICE_ID[priceId] ?? (domain.tier === "dev" ? "Dev" : DEFAULT_TIER_LABEL);
  const promoValue =
    typeof domain.promo_price === "number"
      ? domain.promo_price
      : typeof domain.promoPrice === "number"
      ? domain.promoPrice
      : null;
  if (typeof promoValue === "number" && promoValue > 0) {
    return `${base} (Promo)`;
  }
  return base;
};
